/**
 * Captures the response shape of Next.js server-action fetches so that when
 * the App Router throws E394 ("An unexpected response was received from the
 * server"), we can attach the actual content-type, x-action-redirect, status
 * and length to the Sentry event.
 *
 * Throw site:
 *   node_modules/next/dist/client/components/router-reducer/reducers/server-action-reducer.js
 * The reducer rejects any response that is neither `content-type:
 * text/x-component` nor carries an `x-action-redirect` header. Sentry's
 * default fetch breadcrumb records the URL/status/sizes but NOT the
 * content-type or action-redirect headers needed to tell the failure modes apart.
 *
 * Two responsibilities:
 *   1. `installServerActionCapture()` wraps `window.fetch` once per page,
 *      detects requests carrying the `next-action` header (server actions),
 *      and stores the last few responses' shape in a small ring buffer.
 *   2. `enrichE394Event(event)` is called inside Sentry's `beforeSend` hook
 *      and, when the event is E394, attaches the ring buffer as a context
 *      AND splits the Sentry fingerprint by mechanism + transaction so
 *      unrelated E394 sources don't merge into a single issue.
 *
 * Read-only: never consumes response bodies, never mutates request headers,
 * falls back to the original fetch on any internal error.
 */

import { redactInviteCodeFromUrl } from '@/lib/utils/sentry-url-scrub';

interface CapturedServerActionResponse {
  url: string;
  status: number;
  contentType: string | null;
  xActionRedirect: string | null;
  contentLength: number | null;
  unrecognizedAction: boolean;
  timestamp: number;
}

interface SentryEventLike {
  contexts?: Record<string, unknown>;
  fingerprint?: string[];
  transaction?: string;
  exception?: {
    values?: Array<{
      type?: string;
      value?: string;
      mechanism?: { type?: string };
    }>;
  };
}

const NEXT_ACTION_REQUEST_HEADER = 'next-action';
const RING_BUFFER_SIZE = 5;
const E394_MESSAGE = 'An unexpected response was received from the server.';
const INSTALL_MARKER = '__texServerActionCaptureInstalled';

const ring: CapturedServerActionResponse[] = [];

/**
 * Wraps `window.fetch` so server-action POST responses are recorded into a
 * ring buffer. Idempotent — safe under HMR and double-invocation. No-op on
 * the server.
 */
export function installServerActionCapture(): void {
  if (typeof window === 'undefined') return;
  const currentFetch = window.fetch as typeof window.fetch & {
    [INSTALL_MARKER]?: boolean;
  };
  if (currentFetch[INSTALL_MARKER]) return;

  const originalFetch = currentFetch.bind(window);

  const patched: typeof window.fetch = async function patchedFetch(
    input,
    init
  ) {
    const isServerAction = isServerActionRequest(input, init);
    const response = await originalFetch(input, init);

    if (isServerAction) {
      try {
        recordResponse({
          // Redact at RECORD time: a server action POSTs to the current url, so
          // on /assess/<code> this entry would otherwise hold the invite code
          // (the sole credential to a minor's assessment data).
          url: redactInviteCodeFromUrl(extractUrl(input)),
          status: response.status,
          contentType: response.headers.get('content-type'),
          xActionRedirect: response.headers.get('x-action-redirect'),
          contentLength: parseNumeric(response.headers.get('content-length')),
          unrecognizedAction:
            response.headers.get('x-action-not-found') === '1' ||
            response.headers.get('next-action-not-found') === '1',
          timestamp: Date.now()
        });
      } catch {
        // Never let instrumentation break the original fetch.
      }
    }

    return response;
  };

  (patched as typeof window.fetch & { [INSTALL_MARKER]?: boolean })[
    INSTALL_MARKER
  ] = true;
  window.fetch = patched;
}

/**
 * If `event` is the Next.js E394 error, attaches the recent server-action
 * response shape as a Sentry context and splits the fingerprint by mechanism
 * and transaction. Mutates the event in place. No-op for any other event.
 */
export function enrichE394Event(event: SentryEventLike): void {
  const value = event.exception?.values?.[0]?.value;
  if (value !== E394_MESSAGE) return;

  event.contexts = {
    ...event.contexts,
    next_action_response: {
      // Most-recent first for readability in the Sentry UI.
      recent: ring.slice().reverse(),
      buffer_size: ring.length
    }
  };

  const mechanism = event.exception?.values?.[0]?.mechanism?.type ?? 'unknown';
  // Redact before copying: `beforeSend` calls this BEFORE
  // `scrubInviteCodeFromEvent`, and on /assess/<code> the transaction name
  // contains the invite code.
  const transaction = redactInviteCodeFromUrl(event.transaction ?? 'unknown');
  event.fingerprint = ['next-e394', mechanism, transaction];
}

/** Test-only — clears the ring buffer between tests. */
export function _resetRingForTests(): void {
  ring.length = 0;
}

function isServerActionRequest(
  input: RequestInfo | URL,
  init?: RequestInit
): boolean {
  if (init?.headers) {
    if (new Headers(init.headers).has(NEXT_ACTION_REQUEST_HEADER)) return true;
  }
  if (input instanceof Request) {
    if (input.headers.has(NEXT_ACTION_REQUEST_HEADER)) return true;
  }
  return false;
}

function extractUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

function parseNumeric(value: string | null): number | null {
  if (!value) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function recordResponse(entry: CapturedServerActionResponse): void {
  ring.push(entry);
  if (ring.length > RING_BUFFER_SIZE) ring.shift();
}
